import axios, { AxiosInstance, AxiosError, AxiosResponse } from 'axios'

// API configuration
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL
const API_TIMEOUT = 10000 // 10 seconds

// Validate that API_BASE_URL is configured
if (!API_BASE_URL) {
  throw new Error(
    'VITE_API_BASE_URL environment variable is not set. ' +
    'Please ensure the API Gateway URL is configured during build. ' +
    'For local development, create a .env file with VITE_API_BASE_URL set to your API endpoint.'
  )
}

// Create axios instance with default configuration
export const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: API_TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Request interceptor to add auth token
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('auth_token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor for error handling
apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    return response
  },
  (error: AxiosError) => {
    // Handle common HTTP errors
    if (error.response?.status === 401) {
      // Unauthorized - clear auth and redirect to login
      localStorage.removeItem('auth_token')
      delete apiClient.defaults.headers.common['Authorization']
      
      // Only redirect if not already on login page
      if (window.location.pathname !== '/login') {
        window.location.href = '/login'
      }
    } else if (error.response?.status === 403) {
      // Forbidden - user doesn't have permission
      console.error('Access forbidden:', error.response.data)
    } else if (error.response?.status >= 500) {
      // Server error
      console.error('Server error:', error.response.data)
    } else if (error.code === 'ECONNABORTED') {
      // Timeout error
      console.error('Request timeout')
    }
    
    return Promise.reject(error)
  }
)

// API service methods
export const apiService = {
  // Generic HTTP methods
  get: <T = any>(url: string, config = {}) => 
    apiClient.get<T>(url, config),
  
  post: <T = any>(url: string, data = {}, config = {}) => 
    apiClient.post<T>(url, data, config),
  
  put: <T = any>(url: string, data = {}, config = {}) => 
    apiClient.put<T>(url, data, config),
  
  delete: <T = any>(url: string, config = {}) => 
    apiClient.delete<T>(url, config),
  
  // File upload method - converts file to base64 and sends as JSON
  uploadFile: async (url: string, file: File, additionalData: Record<string, any> = {}) => {
    // Convert file to base64
    const base64 = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = () => {
        const result = reader.result as string
        // Remove data URL prefix to get just the base64 string
        const base64Data = result.split(',')[1]
        resolve(base64Data)
      }
      reader.onerror = reject
      reader.readAsDataURL(file)
    })
    
    // Send as JSON with base64 encoded image
    return apiClient.post(url, {
      image_data: base64,
      filename: file.name,
      content_type: file.type,
      ...additionalData
    })
  },

  // Presigned URL upload method - for large files (bypasses API Gateway limits)
  uploadFilePresigned: async (file: File, partyId: string) => {
    // Step 1: Get presigned URL from backend
    const presignedResponse = await apiClient.post('/upload/presigned', {
      party_id: partyId,
      filename: file.name,
      content_type: file.type,
      file_size: file.size,
    })

    const { upload_url, fields, image_url } = presignedResponse.data

    // Step 2: Upload directly to S3 using presigned URL
    const formData = new FormData()
    
    // Add all fields from presigned post (required by S3)
    Object.entries(fields).forEach(([key, value]) => {
      formData.append(key, value as string)
    })
    
    // Add the file last (S3 requirement)
    formData.append('file', file)

    // Upload directly to S3 (not through our API)
    await axios.post(upload_url, formData, {
      headers: {
        // Don't set Content-Type - browser will set it correctly for multipart
      },
    })

    // Return the final image URL
    return { data: { url: image_url } }
  },
}

export default apiService